// Global Instructions Rule Applied!

import { API_ROUTES, buildFullUrl, getBackendBaseUrl } from '../../../core/utils/endpoints.ts'
import {
  EmailDesignTemplateModel,
  EmailDesignTemplateFilterOptions,
  EmailDesignTemplateModelSchema,
  validateEmailDesignTemplate,
  validateEmailDesignTemplateFilterOptions
} from '../../../state_manager/data_models/email_design_template_model.ts'
import { FilterValue, SortConfig } from '../../../state_manager/interfaces/email.ts'

const BACKEND_BASE_URL = getBackendBaseUrl()

// ── Service response shapes ────────────────────────────────────────────────

export interface EmailDesignTemplatesPaginatedResponse {
  data: EmailDesignTemplateModel[]
  has_more: boolean
  count: number
  error?: string
}

export interface EmailDesignTemplateDetailResponse {
  data: EmailDesignTemplateModel | null
  error?: string
}

export interface EmailDesignTemplateSavePayload {
  name: string
  subject_default: string
  body_source: 'jodit' | 'imported' | 'ckeditor'
  body_html: string
}

export interface EmailDesignTemplateSaveResponse {
  data: { email_design_templates__id: string } | null
  error?: string
}

// ── List / filter ─────────────────────────────────────────────────────────

export const fetchEmailDesignTemplatesFiltered = async (
  sorting: SortConfig[] = [],
  filters: FilterValue[] = [],
  page: number = 1,
  pageSize: number | null = null,
  accessToken: string | null = null
): Promise<EmailDesignTemplatesPaginatedResponse> => {
  try {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' }
    if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`

    const requestBody = {
      data: {
        page,
        page_size: pageSize ?? 200,
        sorting: sorting.map((s) => ({ key: s.key, direction: s.direction })),
        filters: filters.map((f) => ({ key: f.key, value: f.value }))
      }
    }

    const response = await fetch(
      buildFullUrl(BACKEND_BASE_URL, API_ROUTES.EMAIL_DESIGN_TEMPLATES.FILTER),
      {
        method: API_ROUTES.EMAIL_DESIGN_TEMPLATES.FILTER.method,
        headers,
        body: JSON.stringify(requestBody)
      }
    )

    if (!response.ok) {
      if (response.status === 401) throw new Error('Authentication failed - please log in again')
      throw new Error(
        `Failed to fetch email design templates: ${response.status} ${response.statusText}`
      )
    }

    const json = (await response.json()) as {
      data?: unknown[]
      has_more?: boolean
      count?: number
    }
    const rawRows = Array.isArray(json.data) ? json.data : []

    const validated: EmailDesignTemplateModel[] = []
    for (const row of rawRows) {
      try {
        const v = validateEmailDesignTemplate(row) as EmailDesignTemplateModel
        validated.push(v)
      } catch (err) {
        console.warn('Invalid email design template row dropped:', err, row)
      }
    }

    return {
      data: validated,
      has_more: Boolean(json.has_more),
      count: typeof json.count === 'number' ? json.count : validated.length
    }
  } catch (error: any) {
    return {
      data: [],
      has_more: false,
      count: 0,
      error: error?.message || 'Failed to fetch email design templates'
    }
  }
}

// ── Filter options ────────────────────────────────────────────────────────

export const fetchEmailDesignTemplateFilterOptions = async (
  accessToken: string | null = null
): Promise<{ data: EmailDesignTemplateFilterOptions | null; error?: string }> => {
  try {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' }
    if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`

    const response = await fetch(
      buildFullUrl(BACKEND_BASE_URL, API_ROUTES.EMAIL_DESIGN_TEMPLATES.FILTER_OPTIONS),
      {
        method: API_ROUTES.EMAIL_DESIGN_TEMPLATES.FILTER_OPTIONS.method,
        headers
      }
    )

    if (!response.ok) {
      throw new Error(
        `Failed to fetch filter options: ${response.status} ${response.statusText}`
      )
    }

    const json = (await response.json()) as { data?: unknown }
    const validated = validateEmailDesignTemplateFilterOptions(json.data ?? {})
    return { data: validated }
  } catch (error: any) {
    return { data: null, error: error?.message || 'Failed to fetch filter options' }
  }
}

// ── Detail ────────────────────────────────────────────────────────────────

export const fetchEmailDesignTemplateDetail = async (
  templateId: string,
  accessToken: string | null = null
): Promise<EmailDesignTemplateDetailResponse> => {
  try {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' }
    if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`

    const response = await fetch(
      buildFullUrl(BACKEND_BASE_URL, API_ROUTES.EMAIL_DESIGN_TEMPLATES.DETAIL, {
        templateId
      }),
      { method: API_ROUTES.EMAIL_DESIGN_TEMPLATES.DETAIL.method, headers }
    )

    if (!response.ok) {
      if (response.status === 401) throw new Error('Authentication failed')
      throw new Error(
        `Failed to fetch template: ${response.status} ${response.statusText}`
      )
    }

    const json = (await response.json()) as { data?: unknown }
    if (!json.data) return { data: null }

    // Detail returns the full body_html. The model accepts it as optional so
    // the same schema validates grid + detail rows.
    const validated = validateEmailDesignTemplate(json.data) as EmailDesignTemplateModel
    return { data: validated }
  } catch (error: any) {
    return { data: null, error: error?.message || 'Failed to fetch template' }
  }
}

// ── Create / Update / Delete ──────────────────────────────────────────────

export const createEmailDesignTemplate = async (
  payload: EmailDesignTemplateSavePayload,
  accessToken: string | null = null
): Promise<EmailDesignTemplateSaveResponse> => {
  try {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' }
    if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`

    const response = await fetch(
      buildFullUrl(BACKEND_BASE_URL, API_ROUTES.EMAIL_DESIGN_TEMPLATES.CREATE),
      {
        method: API_ROUTES.EMAIL_DESIGN_TEMPLATES.CREATE.method,
        headers,
        body: JSON.stringify({ data: payload })
      }
    )

    const json = (await response.json()) as {
      data?: { email_design_templates__id?: string } | null
      error?: string
    }
    if (!response.ok || json.error) {
      return { data: null, error: json.error || `HTTP ${response.status}` }
    }
    return { data: (json.data ?? null) as { email_design_templates__id: string } | null }
  } catch (error: any) {
    return { data: null, error: error?.message || 'Failed to create template' }
  }
}

export const updateEmailDesignTemplate = async (
  templateId: string,
  payload: EmailDesignTemplateSavePayload,
  accessToken: string | null = null
): Promise<EmailDesignTemplateSaveResponse> => {
  try {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' }
    if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`

    const response = await fetch(
      buildFullUrl(BACKEND_BASE_URL, API_ROUTES.EMAIL_DESIGN_TEMPLATES.UPDATE, {
        templateId
      }),
      {
        method: API_ROUTES.EMAIL_DESIGN_TEMPLATES.UPDATE.method,
        headers,
        body: JSON.stringify({ data: payload })
      }
    )

    const json = (await response.json()) as {
      data?: { email_design_templates__id?: string } | null
      error?: string
    }
    if (!response.ok || json.error) {
      return { data: null, error: json.error || `HTTP ${response.status}` }
    }
    return { data: (json.data ?? null) as { email_design_templates__id: string } | null }
  } catch (error: any) {
    return { data: null, error: error?.message || 'Failed to update template' }
  }
}

export const deleteEmailDesignTemplate = async (
  templateId: string,
  accessToken: string | null = null
): Promise<{ data: { success: boolean } | null; error?: string }> => {
  try {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' }
    if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`

    const response = await fetch(
      buildFullUrl(BACKEND_BASE_URL, API_ROUTES.EMAIL_DESIGN_TEMPLATES.DELETE, {
        templateId
      }),
      { method: API_ROUTES.EMAIL_DESIGN_TEMPLATES.DELETE.method, headers }
    )

    const json = (await response.json()) as {
      data?: { success?: boolean }
      error?: string
    }
    if (!response.ok || json.error) {
      return { data: null, error: json.error || `HTTP ${response.status}` }
    }
    return { data: { success: Boolean(json.data?.success) } }
  } catch (error: any) {
    return { data: null, error: error?.message || 'Failed to delete template' }
  }
}
